import { uiError, paginationLabel, t, ui } from '../i18n';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Grid,
  Pagination,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import {
  AccountTree as BlockIcon,
  Refresh as RefreshIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { useIntegrationLedgerStream } from '../hooks/useIntegrationLedgerStream';
import { getWebappApiUrl, webappFetch } from '../utils/apiConfig';
import {
  IntegrationBlockGroup,
  IntegrationLedgerTransaction,
  formatTime,
  isValidationOperation,
  liveBusinessId,
  liveOperation,
  operationDisplayLabel,
  shortBusinessId,
  shortHash,
  sourceOrgOf,
  targetOrgOf
} from '../domain/integrationLedger';
import {
  IndexStatusChip,
  IntegrationBlockDetailDialog,
  IntegrationTransactionDetailDialog,
  LedgerStatusChip
} from '../components/integration/IntegrationLedgerDialogs';

const ORG_OPTIONS = ['ALL', 'EINS', 'KAIST', 'KULS'];
const ITEMS_PER_PAGE = 12;
const LEDGER_FROM_BLOCK = 0;
const LEDGER_LIMIT = 1000;
const CAPTURE_REPLAY_PARAM = '20260522-morning';
const CAPTURE_BASELINE_CUTOFF_ISO = '2026-05-22T00:00:00.000Z';
const CAPTURE_DASHBOARD_BLOCKS = ['333', '334', '335'];
const CAPTURE_BLOCK_EXPLORER_BLOCKS = ['336', '337', '338'];
const CAPTURE_TRANSACTION_BLOCKS = ['339', '340', '341'];
const CONFIRMED_OPERATION_OPTIONS = ['Daily_Opt', 'Emergency_Opt', 'Validation_Opt', 'assignment', 'retrieval', 'relocation'];

type PaginationState = {
  currentPage: number;
  totalItems: number;
  itemsPerPage: number;
  totalPages: number;
  hasNext: boolean;
  hasPrev: boolean;
};

const EMPTY_PAGINATION: PaginationState = {
  currentPage: 1,
  totalItems: 0,
  itemsPerPage: ITEMS_PER_PAGE,
  totalPages: 1,
  hasNext: false,
  hasPrev: false
};

type CaptureReplaySteps = {
  dashboard: number;
  transactions: number;
  blocks: number;
};

const EMPTY_CAPTURE_STEPS: CaptureReplaySteps = {
  dashboard: 0,
  transactions: 0,
  blocks: 0
};

function boundedCaptureStep(value: unknown, max: number) {
  const step = Number(value || 0);
  if (!Number.isFinite(step)) return 0;
  return Math.max(0, Math.min(step, max));
}

function readCaptureReplaySteps(data: any): CaptureReplaySteps {
  return {
    dashboard: boundedCaptureStep(data?.dashboard?.step, CAPTURE_DASHBOARD_BLOCKS.length),
    transactions: boundedCaptureStep(data?.transactions?.step, CAPTURE_TRANSACTION_BLOCKS.length),
    blocks: boundedCaptureStep(data?.blocks?.step, CAPTURE_BLOCK_EXPLORER_BLOCKS.length)
  };
}

function isBeforeCaptureBaseline(tx: IntegrationLedgerTransaction) {
  const timestamp = new Date(tx.block_created_at).getTime();
  return Number.isFinite(timestamp) && timestamp < new Date(CAPTURE_BASELINE_CUTOFF_ISO).getTime();
}

function blockNumberValue(value: unknown) {
  const parsed = Number(value || 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

function sortLedgerRowsDescending(rows: IntegrationLedgerTransaction[]) {
  return rows.slice().sort((a, b) => (
    blockNumberValue(b.block_number) - blockNumberValue(a.block_number)
    || (b.transaction_index || 0) - (a.transaction_index || 0)
  ));
}

function operationChipColor(operation: string, active = false): 'default' | 'success' | 'warning' {
  if (!active) return 'default';
  return isValidationOperation(operation) ? 'warning' : 'success';
}

function OperationBadge({ operation }: { operation: string }) {
  if (isValidationOperation(operation)) {
    return <Chip size="small" color="warning" variant="outlined" label={ui(operationDisplayLabel(operation))} title={ui(operation)} />;
  }

  return <Typography variant="body2">{operation || '-'}</Typography>;
}

function filterSearch(tx: IntegrationLedgerTransaction, query: string) {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    tx.transaction_id,
    tx.block_number,
    tx.block_hash,
    tx.data_hash,
    tx.sender,
    tx.receiver,
    tx.creator_msp_id,
    tx.chaincode_name,
    tx.chaincode_version,
    tx.function_name,
    tx.record_type,
    tx.business_id_type,
    tx.business_id,
    tx.operation_type,
    tx.payload_hash,
    tx.arg1,
    tx.arg2,
    tx.arg3,
    tx.arg8,
    tx.arg9
  ].filter(Boolean).join(' ').toLowerCase().includes(q);
}

function buildFilteredBlockGroups(
  filteredRows: IntegrationLedgerTransaction[],
  allRows: IntegrationLedgerTransaction[]
): IntegrationBlockGroup[] {
  const allRowsByBlock = new Map<string, IntegrationLedgerTransaction[]>();
  allRows.forEach((tx) => {
    const key = tx.block_number || '-';
    allRowsByBlock.set(key, [...(allRowsByBlock.get(key) || []), tx]);
  });

  const filteredByBlock = new Map<string, IntegrationLedgerTransaction[]>();
  filteredRows.forEach((tx) => {
    const key = tx.block_number || '-';
    filteredByBlock.set(key, [...(filteredByBlock.get(key) || []), tx]);
  });

  return Array.from(filteredByBlock.entries())
    .map(([blockNumber, rows]) => {
      const representative = rows.slice().sort((a, b) => (b.transaction_index || 0) - (a.transaction_index || 0))[0];
      const blockRows = (allRowsByBlock.get(blockNumber) || rows)
        .slice()
        .sort((a, b) => (a.transaction_index || 0) - (b.transaction_index || 0));
      return {
        blockNumber,
        representative,
        rows: blockRows
      };
    })
    .sort((a, b) => Number(b.blockNumber || 0) - Number(a.blockNumber || 0));
}

const BlockExplorer: React.FC = () => {
  const [orgFilter, setOrgFilter] = useState('ALL');
  const [operationFilter, setOperationFilter] = useState('ALL');
  const [query, setQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedBlockNumber, setSelectedBlockNumber] = useState<string | null>(null);
  const [selectedTx, setSelectedTx] = useState<IntegrationLedgerTransaction | null>(null);
  const [pageRows, setPageRows] = useState<IntegrationLedgerTransaction[]>([]);
  const [pagination, setPagination] = useState<PaginationState>(EMPTY_PAGINATION);
  const [pageLoading, setPageLoading] = useState(true);
  const [pageError, setPageError] = useState<string | null>(null);
  const [pageNewBlockNumbers, setPageNewBlockNumbers] = useState<Set<string>>(new Set());
  const [captureReplaySteps, setCaptureReplaySteps] = useState<CaptureReplaySteps>(EMPTY_CAPTURE_STEPS);
  const [captureHighlightedBlocks, setCaptureHighlightedBlocks] = useState<Set<string>>(new Set());
  const replayTimersRef = useRef<number[]>([]);
  const lastCaptureRevisionRef = useRef<number | null>(null);
  const knownPageBlockNumbersRef = useRef<Set<string>>(new Set());
  const initializedPageRef = useRef(false);
  const pageHighlightTimersRef = useRef<number[]>([]);

  const captureReplayMode = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('captureReplay') === CAPTURE_REPLAY_PARAM;
  }, []);

  const ledger = useIntegrationLedgerStream({
    fromBlock: LEDGER_FROM_BLOCK,
    limit: LEDGER_LIMIT,
    enabled: captureReplayMode
  });

  const clearReplayTimers = useCallback(() => {
    replayTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    replayTimersRef.current = [];
  }, []);

  useEffect(() => () => {
    clearReplayTimers();
    pageHighlightTimersRef.current.forEach((timer) => window.clearTimeout(timer));
    pageHighlightTimersRef.current = [];
  }, [clearReplayTimers]);

  const hydratePageRows = useCallback((
    rows: IntegrationLedgerTransaction[],
    nextPagination: PaginationState,
    options: { animateNew?: boolean } = {}
  ) => {
    const sortedRows = sortLedgerRowsDescending(rows);
    const nextBlockNumbers = new Set(sortedRows.map((tx) => String(tx.block_number)).filter(Boolean));
    const shouldAnimateNew = options.animateNew === true;

    if (!shouldAnimateNew) {
      setPageNewBlockNumbers(new Set());
    }

    if (initializedPageRef.current && currentPage === 1 && shouldAnimateNew) {
      const newBlocks = Array.from(nextBlockNumbers).filter((blockNumber) => !knownPageBlockNumbersRef.current.has(blockNumber));
      if (newBlocks.length) {
        setPageNewBlockNumbers((previous) => {
          const merged = new Set(previous);
          newBlocks.forEach((blockNumber) => merged.add(blockNumber));
          return merged;
        });
        const timer = window.setTimeout(() => {
          setPageNewBlockNumbers((previous) => {
            const next = new Set(previous);
            newBlocks.forEach((blockNumber) => next.delete(blockNumber));
            return next;
          });
        }, 4000);
        pageHighlightTimersRef.current.push(timer);
      }
    }

    knownPageBlockNumbersRef.current = nextBlockNumbers;
    initializedPageRef.current = true;
    setPageRows(sortedRows);
    setPagination(nextPagination || EMPTY_PAGINATION);
  }, [currentPage]);

  const loadPage = useCallback(async ({
    initial = false,
    animateNew = false
  }: {
    initial?: boolean;
    animateNew?: boolean;
  } = {}) => {
    if (captureReplayMode) return;
    try {
      if (initial) setPageLoading(true);
      setPageError(null);
      const params = new URLSearchParams({
        fromBlock: '0',
        page: String(currentPage),
        limit: String(ITEMS_PER_PAGE),
        org: orgFilter,
        operation: operationFilter,
        query
      });
      const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/blocks-page?${params.toString()}`);
      const body = await response.json();
      if (!response.ok || !body.success) {
        throw new Error(body.message || body.error || `HTTP ${response.status}`);
      }
      hydratePageRows(body.data.transactions || [], body.data.pagination || EMPTY_PAGINATION, { animateNew });
    } catch (error) {
      setPageError(error instanceof Error ? error.message : 'Unknown error');
    } finally {
      if (initial) setPageLoading(false);
    }
  }, [captureReplayMode, currentPage, hydratePageRows, operationFilter, orgFilter, query]);

  useEffect(() => {
    if (captureReplayMode) return undefined;
    loadPage({ initial: true });
    const timer = window.setInterval(() => loadPage({ animateNew: true }), 2000);
    return () => window.clearInterval(timer);
  }, [captureReplayMode, loadPage]);

  const displayedBlockNumbers = useMemo(
    () => new Set([
      ...CAPTURE_DASHBOARD_BLOCKS.slice(0, captureReplaySteps.dashboard),
      ...CAPTURE_TRANSACTION_BLOCKS.slice(0, captureReplaySteps.transactions),
      ...CAPTURE_BLOCK_EXPLORER_BLOCKS.slice(0, captureReplaySteps.blocks)
    ]),
    [captureReplaySteps]
  );

  const displayedRows = useMemo(
    () => {
      if (!captureReplayMode) return pageRows;
      const rows = captureReplayMode
        ? ledger.transactions.filter((tx) => (
          isBeforeCaptureBaseline(tx) || displayedBlockNumbers.has(String(tx.block_number))
        ))
        : ledger.transactions;
      return sortLedgerRowsDescending(rows);
    },
    [captureReplayMode, displayedBlockNumbers, ledger.transactions, pageRows]
  );

  const filteredRows = useMemo(() => {
    if (!captureReplayMode) return displayedRows;
    return displayedRows.filter((tx) => {
      const source = sourceOrgOf(tx);
      const target = targetOrgOf(tx);
      if (orgFilter !== 'ALL' && source !== orgFilter && target !== orgFilter) return false;
      if (operationFilter !== 'ALL' && liveOperation(tx) !== operationFilter) return false;
      return filterSearch(tx, query);
    });
  }, [captureReplayMode, displayedRows, operationFilter, orgFilter, query]);

  const blockGroups = useMemo(() => buildFilteredBlockGroups(filteredRows, displayedRows), [filteredRows, displayedRows]);
  const totalPages = captureReplayMode
    ? Math.max(1, Math.ceil(blockGroups.length / ITEMS_PER_PAGE))
    : Math.max(1, pagination.totalPages || 1);
  const visibleBlocks = captureReplayMode
    ? blockGroups.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE)
    : blockGroups;
  const selectedBlock = selectedBlockNumber
    ? blockGroups.find((block) => block.blockNumber === selectedBlockNumber) || null
    : null;

  const resetPage = (action: () => void) => {
    setPageNewBlockNumbers(new Set());
    action();
    setCurrentPage(1);
  };

  const showCaptureStep = useCallback((step: number, animate = false) => {
    const boundedStep = boundedCaptureStep(step, CAPTURE_BLOCK_EXPLORER_BLOCKS.length);
    const blockNumber = CAPTURE_BLOCK_EXPLORER_BLOCKS[boundedStep - 1];

    setCaptureReplaySteps((current) => ({ ...current, blocks: boundedStep }));
    setCurrentPage(1);

    if (!animate || !blockNumber) return;

    clearReplayTimers();
    setCaptureHighlightedBlocks(new Set([blockNumber]));
    const timer = window.setTimeout(() => {
      setCaptureHighlightedBlocks((current) => {
        const next = new Set(current);
        next.delete(blockNumber);
        return next;
      });
    }, 4000);
    replayTimersRef.current.push(timer);
  }, [clearReplayTimers]);

  const resetCaptureReplay = useCallback(() => {
    clearReplayTimers();
    setCaptureReplaySteps((current) => ({ ...current, blocks: 0 }));
    setCaptureHighlightedBlocks(new Set());
    setCurrentPage(1);
  }, [clearReplayTimers]);

  useEffect(() => {
    if (!captureReplayMode) return undefined;

    let closed = false;

    const checkCaptureTrigger = async () => {
      try {
        const response = await webappFetch(`${getWebappApiUrl()}/api/blockchain/integration-prototype/capture-replay/state`);
        const body = await response.json();
        const allState = body?.data;
        const state = allState?.blocks;
        if (!state || closed) return;

        const nextSteps = readCaptureReplaySteps(allState);
        const revision = Number(state.revision || 0);

        if (lastCaptureRevisionRef.current === null) {
          lastCaptureRevisionRef.current = revision;
          setCaptureReplaySteps(nextSteps);
          return;
        }

        setCaptureReplaySteps(nextSteps);
        if (revision <= lastCaptureRevisionRef.current) return;

        lastCaptureRevisionRef.current = revision;
        if (state.action === 'reset') {
          resetCaptureReplay();
        } else {
          showCaptureStep(nextSteps.blocks, state.action === 'start');
        }
      } catch {
        // Capture trigger polling is intentionally silent.
      }
    };

    checkCaptureTrigger();
    const timer = window.setInterval(checkCaptureTrigger, 700);
    return () => {
      closed = true;
      window.clearInterval(timer);
    };
  }, [captureReplayMode, resetCaptureReplay, showCaptureStep]);

  const newBlockSx = {
    animation: 'adminBlockNewRowFlash 4s ease-out',
    '@keyframes adminBlockNewRowFlash': {
      '0%': { backgroundColor: 'rgba(46, 125, 50, 0.24)' },
      '18%': { backgroundColor: 'rgba(46, 125, 50, 0.16)' },
      '100%': { backgroundColor: 'transparent' }
    }
  };

  return (
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#1976d2', fontWeight: 'bold' }}>
          <BlockIcon sx={{ fontSize: 34 }} />{t("Block Explorer")}</Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>{t("Explore the latest indexed blockchain blocks. A block detail view shows every indexed transaction in the selected block.")}</Typography>
      </Box>

      {(captureReplayMode ? ledger.error : pageError) && (
        <Alert severity="error" sx={{ mb: 2 }}>{uiError(captureReplayMode ? ledger.error : pageError)}</Alert>
      )}

      <Grid container spacing={2.5} sx={{ mb: 2.5 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Organization")}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                {ORG_OPTIONS.map((option) => (
                  <Chip
                    key={option}
                    clickable
                    color={orgFilter === option ? 'primary' : 'default'}
                    variant={orgFilter === option ? 'filled' : 'outlined'}
                    label={option === 'ALL' ? t("All") : option}
                    onClick={() => resetPage(() => setOrgFilter(option))}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>{t("Operation")}</Typography>
              <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                <Chip
                  clickable
                  color={operationFilter === 'ALL' ? 'success' : 'default'}
                  variant={operationFilter === 'ALL' ? 'filled' : 'outlined'}
                  label={t("All")}
                  onClick={() => resetPage(() => setOperationFilter('ALL'))}
                />
                {CONFIRMED_OPERATION_OPTIONS.map((operation) => (
                  <Chip
                    key={operation}
                    clickable
                    color={operationChipColor(operation, operationFilter === operation)}
                    variant={operationFilter === operation ? 'filled' : 'outlined'}
                    label={ui(operationDisplayLabel(operation))}
                    onClick={() => resetPage(() => setOperationFilter(operation))}
                  />
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, mb: 2.5 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} alignItems={{ xs: 'stretch', md: 'center' }}>
          <TextField
            fullWidth
            size="small"
            value={query}
            onChange={(event) => resetPage(() => setQuery(event.target.value))}
            placeholder={t("Search block hash, Tx ID, business ID, payload hash, organization, or operation")}
            InputProps={{
              startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1 }} />
            }}
          />
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => (captureReplayMode ? ledger.reload() : loadPage({ animateNew: false }))}
            sx={{ whiteSpace: 'nowrap' }}
          >{t("Refresh")}</Button>
        </Stack>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
          <Chip size="small" color="primary" variant="outlined" label={t("{0} blocks", [captureReplayMode ? blockGroups.length : pagination.totalItems])} />
          <Chip size="small" variant="outlined" label={t("{0} transactions", [filteredRows.length])} />
          <Chip size="small" variant="outlined" label={t("{0} shown", [visibleBlocks.length])} />
          <Chip size="small" variant="outlined" label={captureReplayMode ? t("Replay snapshot") : t("Server-paginated PostgreSQL index")} />
        </Stack>
      </Paper>

      <TableContainer component={Paper} sx={{ width: '100%', overflowX: 'auto' }}>
        <Table size="small" sx={{ minWidth: 980 }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>{t("Block")}</strong></TableCell>
              <TableCell><strong>{t("Time")}</strong></TableCell>
              <TableCell><strong>{t("Tx Count")}</strong></TableCell>
              <TableCell><strong>{t("Organizations")}</strong></TableCell>
              <TableCell><strong>{t("Business ID")}</strong></TableCell>
              <TableCell><strong>{t("Operation")}</strong></TableCell>
              <TableCell><strong>{t("Block Hash")}</strong></TableCell>
              <TableCell><strong>{t("Status")}</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {(captureReplayMode ? ledger.loading : pageLoading) ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>
                  <CircularProgress size={26} />
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{t("Syncing block data.")}</Typography>
                </TableCell>
              </TableRow>
            ) : visibleBlocks.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 6 }}>{t("No blocks match the current filters.")}</TableCell>
              </TableRow>
            ) : (
              visibleBlocks.map((block) => {
                const tx = block.representative;
                const orgs = Array.from(new Set(block.rows.map((row) => sourceOrgOf(row) || targetOrgOf(row)).filter(Boolean))).join(', ');
                const filteredCount = filteredRows.filter((row) => row.block_number === block.blockNumber).length;
                return (
                  <TableRow
                    hover
                    key={block.blockNumber}
                    onClick={() => setSelectedBlockNumber(block.blockNumber)}
                    sx={{
                      cursor: 'pointer',
                      ...((captureReplayMode
                        ? ledger.newBlockNumbers.has(block.blockNumber) || captureHighlightedBlocks.has(block.blockNumber)
                        : pageNewBlockNumbers.has(block.blockNumber)
                      ) ? newBlockSx : {})
                    }}
                  >
                    <TableCell sx={{ fontWeight: 'bold' }}>#{block.blockNumber}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatTime(tx.block_created_at)}</TableCell>
                    <TableCell>
                      <Stack spacing={0.5}>
                        <Typography variant="body2">{block.rows.length}</Typography>
                        {filteredCount !== block.rows.length && (
                          <Typography variant="caption" color="text.secondary">{t("Filtered")}{" "}{filteredCount}</Typography>
                        )}
                      </Stack>
                    </TableCell>
                    <TableCell>{orgs || '-'}</TableCell>
                    <TableCell
                      title={liveBusinessId(tx)}
                      sx={{ fontFamily: 'monospace', maxWidth: 210, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                    >
                      {shortBusinessId(liveBusinessId(tx))}
                    </TableCell>
                    <TableCell><OperationBadge operation={liveOperation(tx)} /></TableCell>
                    <TableCell title={tx.block_hash || ''} sx={{ fontFamily: 'monospace', whiteSpace: 'nowrap' }}>
                      {shortHash(tx.block_hash, 10, 8)}
                    </TableCell>
                    <TableCell>
                      <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                        <LedgerStatusChip tx={tx} />
                        <IndexStatusChip tx={tx} />
                      </Stack>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Stack alignItems="center" sx={{ mt: 2 }}>
          <Pagination getItemAriaLabel={paginationLabel} aria-label={t("Pagination")}
            page={currentPage}
            count={totalPages}
            color="primary"
            onChange={(_, page) => setCurrentPage(page)}
          />
        </Stack>
      )}

      <IntegrationBlockDetailDialog
        block={selectedBlock}
        onClose={() => setSelectedBlockNumber(null)}
        onSelectTx={(tx) => {
          setSelectedBlockNumber(null);
          setSelectedTx(tx);
        }}
      />
      <IntegrationTransactionDetailDialog tx={selectedTx} onClose={() => setSelectedTx(null)} />
    </Box>
  );
};

export default BlockExplorer;
